// src/Components/Documents/PersonalDocuments.js
import React, { useState, useEffect, useCallback } from 'react';
import logger from '../../utils/logger';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  Alert,
  Menu,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Breadcrumbs,
  Link,
  useMediaQuery,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent,
  CardActions,
  Grid,
  InputAdornment,
  FormControl,
  Select,
  InputLabel,
  Checkbox,
  Fade
} from '@mui/material';
import {
  Upload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  MoreVert as MoreVertIcon,
  CreateNewFolder as CreateFolderIcon,
  NavigateNext as NavigateNextIcon,
  CloudUpload as CloudUploadIcon,
  Edit as EditIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  ViewComfy as ViewComfyIcon,
  Image as ImageIcon,
  Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  DriveFileMove as MoveIcon,
  DeleteSweep as TrashIcon,
  Restore as RestoreIcon,
  DeleteForever as DeleteForeverIcon,
  Scanner as ScannerIcon,
  Security as VaultIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';
import { documentService } from '../../services/documentService';
import { uploadToFamilyDocumentsWithPath, isFamilyAdmin } from '../../services/familyDocumentService';
import { downloadFile } from '../../utils/downloadUtils';
import { storage } from '../../firebase';
import { ref as storageRef, getBlob } from 'firebase/storage';
import ImageLightbox from './ImageLightbox';
import FolderPickerDialog from './FolderPickerDialog';
import { DocumentScannerDialog } from '../DocumentScanner';

// Extract Firebase Storage path from a download URL (handles legacy files without storagePath field)
const extractStoragePathFromUrl = (url) => {
  try {
    const m = url && url.match(/\/o\/([^?]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
};

/**
 * Helper: Get user-friendly file type label
 */
const getFileTypeLabel = (doc) => {
  // Try to extract extension from filename first
  const fileName = doc.fileName || doc.title || doc.name || '';
  const extension = fileName.split('.').pop()?.toLowerCase();

  if (extension && extension !== fileName.toLowerCase()) {
    return extension.toUpperCase();
  }

  // Fallback: Map MIME types to friendly names
  const mimeType = doc.fileType || doc.mimeType || '';
  const mimeMap = {
    'application/pdf': 'PDF',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint',
    'application/msword': 'Word',
    'application/vnd.ms-excel': 'Excel',
    'application/vnd.ms-powerpoint': 'PowerPoint',
    'text/plain': 'Text',
    'image/jpeg': 'JPEG',
    'image/png': 'PNG',
    'image/gif': 'GIF',
    'image/svg+xml': 'SVG'
  };

  if (mimeMap[mimeType]) {
    return mimeMap[mimeType];
  }

  // Final fallback: extract after slash
  const shortType = mimeType.split('/')[1];
  return shortType && shortType.length < 10 ? shortType.toUpperCase() : 'File';
};

/**
 * PersonalDocuments Component
 *
 * Displays and manages user's personal documents (3-tier architecture - Personal tier)
 * Features:
 * - Upload documents
 * - Download documents
 * - Delete documents
 * - Organize into folders
 * - File type validation
 * - Customizable for Photos (via fileTypeFilter prop)
 *
 * Uses documentService.js for all operations
 */
const PersonalDocuments = ({
  fileTypeFilter = null,           // Filter by MIME type (e.g., 'image/*' for photos) — also filters display list
  acceptFileTypes = null,          // Restrict upload picker only (e.g., '.pdf,.doc,.docx') — does not filter display
  collectionName = 'personalDocuments', // Firestore subcollection name ('personalPhotos' for Photos page)
  defaultView = 'list',             // Default view mode: 'list', 'grid', or 'gallery'
  title = 'My Documents',           // Page title
  uploadButtonText = 'Upload',      // Upload button text
  emptyMessage = 'No documents yet. Upload your first document to get started!'
} = {}) => {
  const { currentUser } = useAuth();
  const { profile } = useProfile();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    const checkAdmin = async () => {
      if (currentUser && profile?.familyCode) {
        const adminStatus = await isFamilyAdmin(profile.familyCode, currentUser.uid);
        setIsAdmin(adminStatus);
      }
    };
    checkAdmin();
  }, [currentUser, profile]);

  // State management
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [currentPath, setCurrentPath] = useState('/');
  const [isDragging, setIsDragging] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    // Use different localStorage key for photos vs documents
    const storageKey = fileTypeFilter === 'image/*' ? 'photosViewMode' : 'personalDocsViewMode';
    return localStorage.getItem(storageKey) || defaultView;
  });

  // Dialog states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);
  const [scannerDialogOpen, setScannerDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadCategory, setUploadCategory] = useState('general');
  const [newFolderName, setNewFolderName] = useState('');
  const [renameValue, setRenameValue] = useState('');

  // Trash view state
  const [viewingTrash, setViewingTrash] = useState(false);

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuItem, setMenuItem] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Search and sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date'); // 'name', 'date', 'size', 'type'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'

  // Image lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Multi-select state
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);

  // Root breadcrumb label based on which collection is in use
  const rootLabel = collectionName === 'personalPhotos' ? 'Personal Photos' : 'Personal Documents';

  /**
   * Load user's personal documents or trash
   */
  const loadDocuments = useCallback(async () => {
    if (!currentUser || !profile?.familyCode) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let docs;
      if (viewingTrash) {
        // Load trashed documents
        docs = await documentService.getTrashedPersonalDocuments(
          profile.familyCode,
          currentUser.uid,
          collectionName
        );
      } else {
        // Load regular documents
        docs = await documentService.listPersonalDocuments(
          profile.familyCode,
          currentUser.uid,
          collectionName
        );
      }

      // Apply fileTypeFilter if provided (e.g., 'image/*' for photos)
      if (fileTypeFilter) {
        docs = docs.filter(doc => {
          // Always show folders
          if (doc.type === 'folder') return true;

          const mimeType = doc.fileType || doc.mimeType || '';

          // Handle wildcard filters (e.g., 'image/*')
          if (fileTypeFilter.includes('/*')) {
            const filterPrefix = fileTypeFilter.split('/*')[0];
            return mimeType.startsWith(filterPrefix + '/');
          }

          // Exact MIME type match
          return mimeType === fileTypeFilter;
        });
      }

      setDocuments(docs);
    } catch (err) {
      logger.error('Error loading personal documents:', err);
      setError(err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [currentUser, profile, fileTypeFilter, viewingTrash, collectionName]);

  // Load documents on mount and when path changes
  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  /**
   * Generate breadcrumb segments from current path
   */
  const getBreadcrumbs = () => {
    const segments = currentPath.split('/').filter(Boolean);
    return segments;
  };

  /**
   * Navigate to breadcrumb segment
   */
  const handleBreadcrumbClick = (index) => {
    const segments = getBreadcrumbs();
    const newPath = '/' + segments.slice(0, index + 1).join('/');
    setCurrentPath(newPath);
  };

  /**
   * Handle drag & drop events
   */
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if leaving the document
    if (e.target === e.currentTarget) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      // Upload files directly to current folder
      await handleUpload(files);
    }
  };

  /**
   * Handle file upload
   */
  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;

    try {
      setUploading(true);
      setUploadProgress(0);

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        await documentService.uploadPersonalDocument(
          profile.familyCode,
          currentUser.uid,
          file,
          currentPath,
          (progress) => {
            const fileProgress = (i / files.length) + (progress / files.length);
            setUploadProgress(Math.round(fileProgress * 100));
          },
          collectionName,
          uploadCategory
        );
      }

      setSnackbar({
        open: true,
        message: `Successfully uploaded ${files.length} file(s)`,
        severity: 'success'
      });

      // Reload documents
      await loadDocuments();
      setUploadDialogOpen(false);
      setSelectedFiles([]);
      setUploadCategory('general');
    } catch (err) {
      logger.error('Error uploading files:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to upload files',
        severity: 'error'
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  /**
   * Handle scan complete - refresh the document list
   */
  const handleScanComplete = useCallback((result) => {
    logger.log('Scan completed:', result);
    setScannerDialogOpen(false);
    // Refresh the documents list
    loadDocuments();
    setSnackbar({
      open: true,
      message: 'Document scanned and saved successfully!',
      severity: 'success'
    });
  }, [loadDocuments]);

  /**
   * Handle file download
   */
  const handleDownload = async (document) => {
    try {
      const filename = document.fileName || document.title || document.name || 'download';
      const url = document.fileUrl || document.downloadURL;
      const storagePath = document.storagePath || extractStoragePathFromUrl(url);

      setSnackbar({ open: true, message: `Downloading ${filename}…`, severity: 'info' });
      await downloadFile(url, filename, storagePath);
      setSnackbar({ open: true, message: `"${filename}" downloaded`, severity: 'success' });
    } catch (err) {
      logger.error('Error downloading file:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to download file',
        severity: 'error'
      });
    }
  };

  /**
   * Handle file deletion (move to trash)
   */
  const handleDelete = async () => {
    if (!selectedFile) return;

    try {
      await documentService.deletePersonalDocument(
        profile.familyCode,
        currentUser.uid,
        selectedFile.id,
        collectionName
      );

      setSnackbar({
        open: true,
        message: `Moved ${selectedFile.name || selectedFile.fileName || selectedFile.title} to trash`,
        severity: 'success'
      });

      // Reload documents
      await loadDocuments();
      setDeleteDialogOpen(false);
      setSelectedFile(null);
    } catch (err) {
      logger.error('Error deleting file:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to delete file',
        severity: 'error'
      });
    }
  };

  /**
   * Handle restore from trash
   */
  const handleRestore = async () => {
    if (!selectedFile) return;

    try {
      await documentService.restorePersonalDocument(
        profile.familyCode,
        currentUser.uid,
        selectedFile.id,
        collectionName
      );

      setSnackbar({
        open: true,
        message: `Restored ${selectedFile.name || selectedFile.fileName || selectedFile.title}`,
        severity: 'success'
      });

      // Reload documents
      await loadDocuments();
      setRestoreDialogOpen(false);
      setSelectedFile(null);
    } catch (err) {
      logger.error('Error restoring file:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to restore file',
        severity: 'error'
      });
    }
  };

  /**
   * Handle permanent deletion
   */
  const handlePermanentDelete = async () => {
    if (!selectedFile) return;

    try {
      await documentService.permanentlyDeletePersonalDocument(
        profile.familyCode,
        currentUser.uid,
        selectedFile.id,
        collectionName
      );

      setSnackbar({
        open: true,
        message: `Permanently deleted ${selectedFile.name || selectedFile.fileName || selectedFile.title}`,
        severity: 'success'
      });

      // Reload documents
      await loadDocuments();
      setPermanentDeleteDialogOpen(false);
      setSelectedFile(null);
    } catch (err) {
      logger.error('Error permanently deleting file:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to permanently delete file',
        severity: 'error'
      });
    }
  };

  /**
   * Handle folder creation
   */
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      await documentService.createFolder(
        profile.familyCode,
        currentUser.uid,
        newFolderName,
        currentPath,
        collectionName
      );

      setSnackbar({
        open: true,
        message: `Created folder "${newFolderName}"`,
        severity: 'success'
      });

      // Reload documents
      await loadDocuments();
      setFolderDialogOpen(false);
      setNewFolderName('');
    } catch (err) {
      logger.error('Error creating folder:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to create folder',
        severity: 'error'
      });
    }
  };

  /**
   * Handle navigation into folder
   */
  const handleFolderClick = (folder) => {
    const folderName = folder.title || folder.name;
    const newPath = currentPath === '/' || currentPath === ''
      ? `/${folderName}`
      : `${currentPath}/${folderName}`;
    setCurrentPath(newPath);
  };

  /**
   * Handle rename file or folder
   */
  const handleRename = async () => {
    if (!renameValue.trim() || !selectedFile) return;

    try {
      const itemType = selectedFile.type === 'folder' ? 'folder' : 'file';

      await documentService.renamePersonalItem(
        profile.familyCode,
        currentUser.uid,
        selectedFile.id,
        renameValue,
        itemType,
        collectionName
      );

      setSnackbar({
        open: true,
        message: `Renamed to "${renameValue}"`,
        severity: 'success'
      });

      // Reload documents
      await loadDocuments();
      setRenameDialogOpen(false);
      setRenameValue('');
      setSelectedFile(null);
    } catch (err) {
      logger.error('Error renaming item:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to rename item',
        severity: 'error'
      });
    }
  };

  /**
   * Handle move file or folder to another folder
   */
  const handleMove = async (destinationPath) => {
    if (!selectedFile) return;

    try {
      await documentService.movePersonalDocument(
        profile.familyCode,
        currentUser.uid,
        selectedFile.id,
        destinationPath,
        collectionName
      );

      const itemName = selectedFile.name || selectedFile.fileName || 'Item';
      const destDisplay = destinationPath === '/' ? 'Root' : destinationPath;

      setSnackbar({
        open: true,
        message: `Moved "${itemName}" to ${destDisplay}`,
        severity: 'success'
      });

      // Reload documents
      await loadDocuments();
      setMoveDialogOpen(false);
      setSelectedFile(null);
    } catch (err) {
      logger.error('Error moving item:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to move item',
        severity: 'error'
      });
    }
  };

  /**
   * Handle move file to Vault (cross-storage move)
   */
  const handleMoveToVault = async (file) => {
    if (!file || file.type === 'folder') {
      setSnackbar({
        open: true,
        message: 'Only files can be moved to Vault',
        severity: 'warning'
      });
      return;
    }

    try {
      await documentService.movePersonalToVault(
        profile.familyCode,
        currentUser.uid,
        file.id,
        collectionName
      );

      setSnackbar({
        open: true,
        message: `"${file.name || file.fileName}" moved to Vault successfully`,
        severity: 'success'
      });
      await loadDocuments();
    } catch (err) {
      logger.error('Error moving to vault:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to move file to Vault',
        severity: 'error'
      });
    }
  };

  /**
   * Handle copy file to Vault (keeps original in place)
   */
  const handleCopyToVault = async (file) => {
    if (!file || file.type === 'folder') {
      setSnackbar({ open: true, message: 'Only files can be copied to Vault', severity: 'warning' });
      return;
    }

    try {
      const path = file.storagePath || extractStoragePathFromUrl(file.fileUrl || file.downloadURL);
      if (!path) throw new Error('Cannot resolve storage path for this file');
      const blob = await getBlob(storageRef(storage, path));
      const fileObj = new File([blob], file.fileName || file.name || file.title, { type: file.fileType || file.mimeType || blob.type });

      await documentService.uploadVaultDocument(profile.familyCode, currentUser.uid, fileObj, '');

      setSnackbar({ open: true, message: `"${file.fileName || file.name || file.title}" copied to Vault`, severity: 'success' });
    } catch (err) {
      logger.error('Error copying to vault:', err);
      setSnackbar({ open: true, message: err.message || 'Failed to copy to Vault', severity: 'error' });
    }
  };

  /**
   * Handle copy to family documents
   */
  const handleCopyToFamily = async (file) => {
    if (!file || file.type === 'folder') {
      setSnackbar({ open: true, message: 'Only files can be copied to Family Documents', severity: 'warning' });
      return;
    }

    try {
      const path = file.storagePath || extractStoragePathFromUrl(file.fileUrl || file.downloadURL);
      if (!path) throw new Error('Cannot resolve storage path for this file');
      const blob = await getBlob(storageRef(storage, path));
      const fileObj = new File([blob], file.fileName || file.name || file.title, { type: file.fileType || file.mimeType || blob.type });

      const storageType = (file.fileType || file.mimeType || '').startsWith('image/') ? 'photos' : 'documents';

      await uploadToFamilyDocumentsWithPath(profile.familyCode, fileObj, {
        category: file.category || 'general',
        uploadedBy: currentUser.uid,
        uploadedByName: profile?.displayName || `${profile?.firstName} ${profile?.lastName}` || currentUser.email,
        storageType
      }, '/');

      setSnackbar({ open: true, message: `"${file.fileName || file.name || file.title}" copied to Family Documents`, severity: 'success' });
    } catch (err) {
      logger.error('Error copying to family documents:', err);
      setSnackbar({ open: true, message: err.message || 'Failed to copy to Family Documents', severity: 'error' });
    }
  };

  /**
   * Toggle selection of an item
   */
  const toggleItemSelection = (item) => {
    setSelectedItems(prev => {
      const isSelected = prev.some(i => i.id === item.id);
      if (isSelected) {
        return prev.filter(i => i.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
    if (!selectionMode) {
      setSelectionMode(true);
    }
  };

  /**
   * Toggle select all items in current folder
   */
  const toggleSelectAll = () => {
    const currentFolderItems = getProcessedDocuments();
    if (selectedItems.length === currentFolderItems.length) {
      // Deselect all
      setSelectedItems([]);
      setSelectionMode(false);
    } else {
      // Select all
      setSelectedItems(currentFolderItems);
      setSelectionMode(true);
    }
  };

  /**
   * Clear selection
   */
  const clearSelection = () => {
    setSelectedItems([]);
    setSelectionMode(false);
  };

  /**
   * Handle batch delete
   */
  const handleBatchDelete = async () => {
    if (selectedItems.length === 0) return;

    const confirmMessage = `Are you sure you want to delete ${selectedItems.length} item(s)?`;
    if (!window.confirm(confirmMessage)) return;

    try {
      // Delete all selected items in parallel
      await Promise.all(
        selectedItems.map(item =>
          documentService.deletePersonalDocument(
            profile.familyCode,
            currentUser.uid,
            item.id,
            collectionName
          )
        )
      );

      setSnackbar({
        open: true,
        message: `Successfully deleted ${selectedItems.length} item(s)`,
        severity: 'success'
      });

      clearSelection();
      await loadDocuments();
    } catch (err) {
      logger.error('Error deleting items:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to delete some items',
        severity: 'error'
      });
    }
  };

  /**
   * Handle batch move
   */
  const handleBatchMove = (destinationPath) => {
    if (selectedItems.length === 0) return;

    const moveItems = async () => {
      try {
        await Promise.all(
          selectedItems.map(item =>
            documentService.movePersonalDocument(
              profile.familyCode,
              currentUser.uid,
              item.id,
              destinationPath,
              collectionName
            )
          )
        );

        const destDisplay = destinationPath === '/' ? 'Root' : destinationPath;
        setSnackbar({
          open: true,
          message: `Moved ${selectedItems.length} item(s) to ${destDisplay}`,
          severity: 'success'
        });

        clearSelection();
        await loadDocuments();
        setMoveDialogOpen(false);
      } catch (err) {
        logger.error('Error moving items:', err);
        setSnackbar({
          open: true,
          message: err.message || 'Failed to move some items',
          severity: 'error'
        });
      }
    };

    moveItems();
  };

  /**
   * Handle batch download
   */
  const handleBatchDownload = () => {
    if (selectedItems.length === 0) return;

    // Only download files, not folders
    const files = selectedItems.filter(item => item.type !== 'folder');

    if (files.length === 0) {
      setSnackbar({
        open: true,
        message: 'No files selected to download (folders cannot be downloaded)',
        severity: 'warning'
      });
      return;
    }

    // Download each file
    files.forEach(file => {
      if (file.fileUrl) {
        window.open(file.fileUrl, '_blank');
      }
    });

    setSnackbar({
      open: true,
      message: `Downloading ${files.length} file(s)`,
      severity: 'success'
    });
  };

  /**
   * Open context menu
   */
  const handleMenuOpen = (event, item) => {
    setMenuAnchor(event.currentTarget);
    setMenuItem(item);
  };

  /**
   * Close context menu
   */
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuItem(null);
  };

  /**
   * Handle view mode change
   */
  const handleViewModeChange = (event, newView) => {
    if (newView !== null) {
      setViewMode(newView);
      const storageKey = fileTypeFilter === 'image/*' ? 'photosViewMode' : 'personalDocsViewMode';
      localStorage.setItem(storageKey, newView);
    }
  };

  /**
   * Check if file is an image
   */
  const isImage = (doc) => {
    const mimeType = doc.fileType || doc.mimeType || '';
    return mimeType.startsWith('image/');
  };

  /**
   * Get filtered documents based on view mode
   */
  const getFilteredDocuments = () => {
    if (viewMode === 'gallery') {
      // Gallery view only shows images
      return documents.filter(doc => doc.type === 'folder' || isImage(doc));
    }
    return documents;
  };

  /**
   * Apply search filter to documents
   */
  const applySearch = (docs) => {
    if (!searchQuery.trim()) return docs;

    const query = searchQuery.toLowerCase();
    return docs.filter(doc => {
      const name = (doc.title || doc.fileName || doc.name || '').toLowerCase();
      const type = doc.type || '';
      return name.includes(query) || type.includes(query);
    });
  };

  /**
   * Apply sorting to documents
   */
  const applySorting = (docs) => {
    const sorted = [...docs].sort((a, b) => {
      // Always keep folders at the top
      if (a.type === 'folder' && b.type !== 'folder') return -1;
      if (a.type !== 'folder' && b.type === 'folder') return 1;

      let comparison = 0;

      switch (sortBy) {
        case 'name':
          const nameA = (a.title || a.fileName || a.name || '').toLowerCase();
          const nameB = (b.title || b.fileName || b.name || '').toLowerCase();
          comparison = nameA.localeCompare(nameB);
          break;

        case 'date':
          const dateA = (a.updatedAt || a.createdAt)?.toMillis?.() || 0;
          const dateB = (b.updatedAt || b.createdAt)?.toMillis?.() || 0;
          comparison = dateA - dateB;
          break;

        case 'size':
          const sizeA = a.fileSize || a.size || 0;
          const sizeB = b.fileSize || b.size || 0;
          comparison = sizeA - sizeB;
          break;

        case 'type':
          const typeA = getFileTypeLabel(a);
          const typeB = getFileTypeLabel(b);
          comparison = typeA.localeCompare(typeB);
          break;

        default:
          comparison = 0;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return sorted;
  };

  /**
   * Get processed documents (filtered, searched, sorted)
   */
  const getProcessedDocuments = () => {
    let docs = getFilteredDocuments();
    docs = applySearch(docs);
    docs = applySorting(docs);
    return docs;
  };

  /**
   * Toggle sort order
   */
  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  /**
   * Open image in lightbox
   */
  const handleImageClick = (clickedDoc) => {
    // Get all images in current folder
    const currentFolderDocs = getProcessedDocuments().filter((doc) => {
      const docFolder = doc.folderPath || '/';
      const current = currentPath || '/';
      return docFolder === current && doc.type !== 'folder' && isImage(doc);
    });

    // Find index of clicked image
    const index = currentFolderDocs.findIndex(doc => doc.id === clickedDoc.id);

    if (index !== -1) {
      setLightboxImages(currentFolderDocs);
      setLightboxIndex(index);
      setLightboxOpen(true);
    }
  };

  /**
   * Format file size for display
   */
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  /**
   * Format date for display
   */
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (!currentUser) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Please sign in to access your documents.</Alert>
      </Box>
    );
  }

  if (!profile?.familyCode) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Please join a family to access documents.</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{ p: 3, position: 'relative' }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'flex-start' }, gap: 1, mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" gutterBottom>
            {title}
          </Typography>

          {/* Breadcrumb Navigation */}
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="folder breadcrumb navigation"
            sx={{ mt: 1 }}
          >
            {viewingTrash ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main' }}>
                <TrashIcon fontSize="small" />
                <Typography variant="body2" fontWeight={600}>
                  Trash
                </Typography>
              </Box>
            ) : [
              /* Root: Personal Documents */
              currentPath === '/' ? (
                <Typography
                  key="root"
                  variant="body2"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'primary.main',
                    fontWeight: 600
                  }}
                >
                  <FolderIcon fontSize="small" />
                  {rootLabel}
                </Typography>
              ) : (
                <Link
                  key="root"
                  component="button"
                  variant="body2"
                  onClick={() => setCurrentPath('/')}
                  underline="hover"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'text.primary',
                    fontWeight: 400,
                    cursor: 'pointer'
                  }}
                >
                  <FolderIcon fontSize="small" />
                  {rootLabel}
                </Link>
              ),
              /* Folder path segments */
              ...getBreadcrumbs().map((segment, index) => {
                const isLast = index === getBreadcrumbs().length - 1;
                return isLast ? (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 600
                    }}
                  >
                    {segment}
                  </Typography>
                ) : (
                  <Link
                    key={index}
                    component="button"
                    variant="body2"
                    onClick={() => handleBreadcrumbClick(index)}
                    underline="hover"
                    sx={{
                      color: 'text.primary',
                      fontWeight: 400,
                      cursor: 'pointer'
                    }}
                  >
                    {segment}
                  </Link>
                );
              })
            ]}
          </Breadcrumbs>
        </Box>

        {/* Toolbar Buttons - Show selection toolbar or main toolbar */}
        {selectedItems.length > 0 ? (
          <Fade in={selectedItems.length > 0}>
            <Box sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              bgcolor: 'primary.light',
              color: 'primary.contrastText',
              px: 2,
              py: 1,
              borderRadius: 1
            }}>
              <Typography variant="body2" sx={{ mr: 1, fontWeight: 600 }}>
                {selectedItems.length} selected
              </Typography>
              <Button
                size="small"
                variant="outlined"
                sx={{ color: 'inherit', borderColor: 'currentColor' }}
                startIcon={<MoveIcon />}
                onClick={() => setMoveDialogOpen(true)}
              >
                Move
              </Button>
              <Button
                size="small"
                variant="outlined"
                sx={{ color: 'inherit', borderColor: 'currentColor' }}
                startIcon={<DownloadIcon />}
                onClick={handleBatchDownload}
              >
                Download
              </Button>
              <Button
                size="small"
                variant="outlined"
                sx={{ color: 'inherit', borderColor: 'currentColor' }}
                startIcon={<DeleteIcon />}
                onClick={handleBatchDelete}
              >
                Delete
              </Button>
              <Button
                size="small"
                variant="text"
                sx={{ color: 'inherit', ml: 'auto' }}
                onClick={clearSelection}
              >
                Clear
              </Button>
            </Box>
          </Fade>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' } }}>
            {!viewingTrash && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<CreateFolderIcon />}
                  onClick={() => setFolderDialogOpen(true)}
                  size="small"
                  fullWidth={isMobile}
                >
                  New Folder
                </Button>
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  onClick={() => setUploadDialogOpen(true)}
                  size="small"
                  fullWidth={isMobile}
                >
                  {uploadButtonText}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ScannerIcon />}
                  onClick={() => setScannerDialogOpen(true)}
                  size="small"
                  fullWidth={isMobile}
                >
                  Scan
                </Button>
              </>
            )}
            <Button
              variant={viewingTrash ? "contained" : "outlined"}
              startIcon={<TrashIcon />}
              onClick={() => {
                setViewingTrash(!viewingTrash);
                setCurrentPath('/');
                setSelectedItems([]);
                setSelectionMode(false);
              }}
              size="small"
              fullWidth={isMobile}
              color={viewingTrash ? "primary" : "inherit"}
            >
              {viewingTrash ? 'Back' : 'Trash'}
            </Button>
          </Box>
        )}
      </Box>

      {/* Search, Sort, and View Controls */}
      <Box sx={{
        display: 'flex',
        gap: 2,
        mb: 2,
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between'
      }}>
        {/* Search Field */}
        <TextField
          size="small"
          placeholder="Search files and folders..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: isMobile ? '100%' : 250,
            maxWidth: isMobile ? '100%' : 400
          }}
        />

        {/* Sort and View Controls */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Sort Dropdown */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="sort-by-label">Sort by</InputLabel>
            <Select
              labelId="sort-by-label"
              value={sortBy}
              label="Sort by"
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="date">Date</MenuItem>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="size">Size</MenuItem>
              <MenuItem value="type">Type</MenuItem>
            </Select>
          </FormControl>

          {/* Sort Order Toggle */}
          <IconButton
            size="small"
            onClick={toggleSortOrder}
            title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
          >
            {sortOrder === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
          </IconButton>

          {/* View Mode Toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            aria-label="view mode"
          >
            <ToggleButton value="list" aria-label="list view">
              <ViewListIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="grid" aria-label="grid view">
              <ViewModuleIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="gallery" aria-label="gallery view">
              <ViewComfyIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Error display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading state */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <LinearProgress sx={{ width: '100%' }} />
        </Box>
      )}

      {/* Documents Display */}
      {!loading && documents.length === 0 && (
        <Alert severity="info">
          {viewingTrash ? 'Trash is empty.' : emptyMessage}
        </Alert>
      )}

      {!loading && documents.length > 0 && (() => {
        const filteredDocs = getProcessedDocuments().filter((doc) => {
          const docFolder = doc.folderPath || '/';
          const current = currentPath || '/';
          return docFolder === current;
        });

        if (filteredDocs.length === 0 && viewMode === 'gallery') {
          return (
            <Alert severity="info" sx={{ mt: 2 }}>
              No images in this folder. Switch to List or Grid view to see all files.
            </Alert>
          );
        }

        // LIST VIEW
        if (viewMode === 'list') {
          return (
            <TableContainer component={Paper} sx={{
              mt: 2,
              overflowX: 'auto',
              '& .MuiTable-root': {
                minWidth: isMobile ? 500 : 650
              }
            }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedItems.length > 0 && selectedItems.length < filteredDocs.length}
                        checked={filteredDocs.length > 0 && selectedItems.length === filteredDocs.length}
                        onChange={toggleSelectAll}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Size</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Date Modified</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDocs.map((doc) => {
                    const isSelected = selectedItems.some(item => item.id === doc.id);
                    return (
                      <TableRow
                        key={doc.id}
                        hover
                        selected={isSelected}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleItemSelection(doc);
                            }}
                            size="small"
                          />
                        </TableCell>
                        <TableCell
                          onClick={() => {
                            if (doc.type === 'folder') {
                              handleFolderClick(doc);
                            } else if (isImage(doc)) {
                              handleImageClick(doc);
                            } else {
                              handleDownload(doc);
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {doc.type === 'folder' ? (
                              <FolderIcon color="primary" fontSize="small" />
                            ) : (
                              <FileIcon color="action" fontSize="small" />
                            )}
                            <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 140, sm: 250 } }}>
                              {doc.title || doc.fileName || doc.name || 'Untitled'}
                            </Typography>
                          </Box>
                        </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {doc.type === 'folder' ? '-' : formatFileSize(doc.fileSize || doc.size || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {doc.type === 'folder' ? 'Folder' : getFileTypeLabel(doc)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(doc.updatedAt || doc.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuOpen(e, doc);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          );
        }

        // GRID VIEW
        if (viewMode === 'grid') {
          return (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {filteredDocs.map((doc) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={doc.id}>
                  <Card
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      '&:hover': { boxShadow: 4 }
                    }}
                    onClick={() => {
                      if (doc.type === 'folder') {
                        handleFolderClick(doc);
                      } else if (isImage(doc)) {
                        handleImageClick(doc);
                      } else {
                        handleDownload(doc);
                      }
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', pb: 1 }}>
                      <Box sx={{ mb: 2, mt: 1 }}>
                        {doc.type === 'folder' ? (
                          <FolderIcon color="primary" sx={{ fontSize: 64 }} />
                        ) : isImage(doc) && doc.fileUrl ? (
                          <Box
                            component="img"
                            src={doc.fileUrl}
                            alt={doc.fileName || 'Image'}
                            sx={{
                              width: '100%',
                              height: 120,
                              objectFit: 'cover',
                              borderRadius: 1
                            }}
                          />
                        ) : (
                          <FileIcon color="action" sx={{ fontSize: 64 }} />
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        noWrap
                        title={doc.title || doc.fileName || doc.name}
                      >
                        {doc.title || doc.fileName || doc.name || 'Untitled'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {doc.type === 'folder' ? 'Folder' : formatFileSize(doc.fileSize || 0)}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'center', pt: 0 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, doc);
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          );
        }

        // GALLERY VIEW (Images only)
        if (viewMode === 'gallery') {
          return (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {filteredDocs.map((doc) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={doc.id}>
                  <Card
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      '&:hover': { boxShadow: 6 }
                    }}
                    onClick={() => {
                      if (doc.type === 'folder') {
                        handleFolderClick(doc);
                      } else {
                        handleImageClick(doc);
                      }
                    }}
                  >
                    <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                      {doc.type === 'folder' ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <FolderIcon color="primary" sx={{ fontSize: 80 }} />
                          <Typography variant="body2" fontWeight={500} sx={{ mt: 1, px: 2 }}>
                            {doc.name || 'Folder'}
                          </Typography>
                        </Box>
                      ) : (
                        <>
                          <Box
                            component="img"
                            src={doc.fileUrl || ''}
                            alt={doc.fileName || 'Image'}
                            sx={{
                              width: '100%',
                              height: 200,
                              objectFit: 'cover'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <Box
                            sx={{
                              display: 'none',
                              width: '100%',
                              height: 200,
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'action.hover'
                            }}
                          >
                            <ImageIcon sx={{ fontSize: 80, color: 'action.disabled' }} />
                          </Box>
                          <Box sx={{ p: 1 }}>
                            <Typography variant="caption" fontWeight={500} noWrap>
                              {doc.fileName || 'Image'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {formatFileSize(doc.fileSize || 0)}
                            </Typography>
                          </Box>
                        </>
                      )}
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-end', pt: 0, px: 1, pb: 1 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, doc);
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          );
        }

        return null;
      })()}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {viewingTrash ? (
          // Trash view menu options
          [
            <MenuItem key="restore" onClick={() => {
              setSelectedFile(menuItem);
              setRestoreDialogOpen(true);
              handleMenuClose();
            }}>
              <RestoreIcon sx={{ mr: 1 }} fontSize="small" />
              Restore
            </MenuItem>,
            <MenuItem key="delete-forever" onClick={() => {
              setSelectedFile(menuItem);
              setPermanentDeleteDialogOpen(true);
              handleMenuClose();
            }}>
              <DeleteForeverIcon sx={{ mr: 1 }} fontSize="small" color="error" />
              Delete Forever
            </MenuItem>
          ]
        ) : (
          // Regular view menu options
          [
            menuItem?.type !== 'folder' && (
              <MenuItem key="download" onClick={() => {
                handleDownload(menuItem);
                handleMenuClose();
              }}>
                <DownloadIcon sx={{ mr: 1 }} fontSize="small" />
                Download
              </MenuItem>
            ),
            <MenuItem key="rename" onClick={() => {
              setSelectedFile(menuItem);
              setRenameValue(menuItem?.name || menuItem?.fileName || '');
              setRenameDialogOpen(true);
              handleMenuClose();
            }}>
              <EditIcon sx={{ mr: 1 }} fontSize="small" />
              Rename
            </MenuItem>,
            <MenuItem key="move" onClick={() => {
              setSelectedFile(menuItem);
              setMoveDialogOpen(true);
              handleMenuClose();
            }}>
              <MoveIcon sx={{ mr: 1 }} fontSize="small" />
              Move to Folder
            </MenuItem>,
            isAdmin && menuItem?.type !== 'folder' && (
              <MenuItem key="copy-to-family" onClick={() => {
                handleCopyToFamily(menuItem);
                handleMenuClose();
              }}>
                <CopyIcon sx={{ mr: 1 }} fontSize="small" color="secondary" />
                Copy to Family Documents
              </MenuItem>
            ),
            menuItem?.type !== 'folder' && (
              <MenuItem key="copy-to-vault" onClick={() => {
                handleCopyToVault(menuItem);
                handleMenuClose();
              }}>
                <VaultIcon sx={{ mr: 1 }} fontSize="small" color="secondary" />
                Copy to Vault
              </MenuItem>
            ),
            menuItem?.type !== 'folder' && (
              <MenuItem key="move-to-vault" onClick={() => {
                handleMoveToVault(menuItem);
                handleMenuClose();
              }}>
                <VaultIcon sx={{ mr: 1 }} fontSize="small" color="primary" />
                Move to Vault
              </MenuItem>
            ),
            <MenuItem key="delete" onClick={() => {
              setSelectedFile(menuItem);
              setDeleteDialogOpen(true);
              handleMenuClose();
            }}>
              <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
              Move to Trash
            </MenuItem>
          ].filter(Boolean)
        )}
      </Menu>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => !uploading && setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>{title || 'Upload Documents'}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="caption">
              {collectionName === 'personalPhotos'
                ? 'Photos are private to you. You can copy or move them to the Vault or Family Photos at any time.'
                : 'Documents are private to you. You can copy or move them to the Vault or Family Documents at any time.'}
            </Typography>
          </Alert>
          <Typography variant="caption" color="text.secondary">
            Maximum file size: 100MB
          </Typography>
          <Box sx={{ mt: 2, mb: selectedFiles.length > 0 ? 1 : 2 }}>
            <input
              type="file"
              multiple
              accept={acceptFileTypes || fileTypeFilter || undefined}
              onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
              style={{ width: '100%' }}
            />
          </Box>
          {selectedFiles.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Selected files ({selectedFiles.length}):
              </Typography>
              {selectedFiles.map((file, index) => (
                <Typography key={index} variant="caption" display="block">
                  • {file.name} ({formatFileSize(file.size)})
                </Typography>
              ))}
            </Box>
          )}
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={uploadCategory}
              label="Category"
              onChange={(e) => setUploadCategory(e.target.value)}
            >
              {collectionName === 'personalPhotos' ? [
                <MenuItem key="general" value="general">General</MenuItem>,
                <MenuItem key="family-events" value="family-events">Family Events</MenuItem>,
                <MenuItem key="vacations" value="vacations">Vacations</MenuItem>,
                <MenuItem key="holidays" value="holidays">Holidays</MenuItem>,
                <MenuItem key="celebrations" value="celebrations">Celebrations</MenuItem>,
                <MenuItem key="portraits" value="portraits">Portraits</MenuItem>,
                <MenuItem key="other" value="other">Other</MenuItem>
              ] : [
                <MenuItem key="general" value="general">General</MenuItem>,
                <MenuItem key="legal" value="legal">Legal</MenuItem>,
                <MenuItem key="financial" value="financial">Financial</MenuItem>,
                <MenuItem key="health" value="health">Health</MenuItem>,
                <MenuItem key="education" value="education">Education</MenuItem>,
                <MenuItem key="personal" value="personal">Personal</MenuItem>,
                <MenuItem key="other" value="other">Other</MenuItem>
              ]}
            </Select>
          </FormControl>
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="caption" sx={{ mt: 1 }}>
                Uploading... {uploadProgress}%
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={() => handleUpload(selectedFiles)}
            disabled={uploading || selectedFiles.length === 0}
            variant="contained"
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog (Move to Trash) */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        fullScreen={isMobile}
      >
        <DialogTitle>Move to Trash?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to move "{selectedFile?.name || selectedFile?.fileName || selectedFile?.title}" to trash?
          </Typography>
          <Typography variant="caption" color="text.secondary">
            You can restore this item from the trash later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="warning" variant="contained">
            Move to Trash
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore Confirmation Dialog */}
      <Dialog
        open={restoreDialogOpen}
        onClose={() => setRestoreDialogOpen(false)}
        fullScreen={isMobile}
      >
        <DialogTitle>Restore from Trash?</DialogTitle>
        <DialogContent>
          <Typography>
            Restore "{selectedFile?.name || selectedFile?.fileName || selectedFile?.title}" to its original location?
          </Typography>
          <Typography variant="caption" color="text.secondary">
            The item will be moved back to your documents.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleRestore} color="primary" variant="contained">
            Restore
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permanent Delete Confirmation Dialog */}
      <Dialog
        open={permanentDeleteDialogOpen}
        onClose={() => setPermanentDeleteDialogOpen(false)}
        fullScreen={isMobile}
      >
        <DialogTitle>Permanently Delete?</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              Warning: This action cannot be undone!
            </Typography>
          </Alert>
          <Typography>
            Are you sure you want to permanently delete "{selectedFile?.name || selectedFile?.fileName || selectedFile?.title}"?
          </Typography>
          <Typography variant="caption" color="text.secondary">
            This will delete the file from storage permanently. You cannot restore it after deletion.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermanentDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handlePermanentDelete} color="error" variant="contained">
            Delete Forever
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Folder Dialog */}
      <Dialog
        open={folderDialogOpen}
        onClose={() => setFolderDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Create New Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleCreateFolder();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFolderDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateFolder}
            variant="contained"
            disabled={!newFolderName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog
        open={renameDialogOpen}
        onClose={() => setRenameDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Rename {selectedFile?.type === 'folder' ? 'Folder' : 'File'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Name"
            fullWidth
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleRename();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleRename}
            variant="contained"
            disabled={!renameValue.trim()}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drag & Drop Overlay */}
      {isDragging && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(25, 118, 210, 0.1)',
            border: '3px dashed',
            borderColor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            pointerEvents: 'none'
          }}
        >
          <Paper
            elevation={8}
            sx={{
              p: 4,
              backgroundColor: 'background.paper',
              borderRadius: 2,
              textAlign: 'center'
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" color="primary" gutterBottom>
              Drop files here to upload
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload to: {currentPath === '/' ? 'Root' : currentPath}
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Image Lightbox */}
      <ImageLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={lightboxImages}
        currentIndex={lightboxIndex}
        onNavigate={setLightboxIndex}
      />

      {/* Folder Picker Dialog for Move */}
      <FolderPickerDialog
        open={moveDialogOpen}
        onClose={() => {
          setMoveDialogOpen(false);
          setSelectedFile(null);
        }}
        onSelect={selectedItems.length > 0 ? handleBatchMove : handleMove}
        allDocuments={documents}
        currentPath={selectedFile?.folderPath || '/'}
        title={selectedItems.length > 0 ? `Move ${selectedItems.length} item(s) to Folder` : "Move to Folder"}
      />

      {/* Document Scanner Dialog */}
      <DocumentScannerDialog
        open={scannerDialogOpen}
        onClose={() => setScannerDialogOpen(false)}
        onComplete={handleScanComplete}
        destinationPath={currentPath}
        destinationType="personal"
        storageStrategy="documentService"
      />
    </Box>
  );
};

export default PersonalDocuments;
